import * as Sentry from '@sentry/react';

export interface RetryOptions {
  maxRetries?: number;
  onRetry?: (error?: unknown) => Promise<boolean | undefined> | Promise<void>;
  onFailure?: (error: unknown) => void;
  name?: string;
  sentry?: {
    tag: string;
    extra?: Record<string, unknown>;
  };
  /** When true, onFailure is skipped if onRetry already handled the error (returned false) */
  skipFailureIfHandled?: boolean;
}

export const withRetry = async <T>(
  attemptFn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> => {
  const {
    maxRetries = 1,
    onRetry,
    onFailure,
    name = 'noName',
    sentry,
    skipFailureIfHandled = false,
  } = options;

  let lastError: unknown;
  let handledByRetry = false;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await attemptFn();
    } catch (error) {
      lastError = error;
      console.error(`${name}: Attempt ${attempt + 1} failed`, error);

      if (attempt < maxRetries && onRetry) {
        try {
          const shouldContinue = await onRetry(error);
          if (shouldContinue === false) {
            handledByRetry = true;
            break;
          }
        } catch (retryError) {
          lastError = retryError;
          break;
        }
      }
    }
  }

  if (lastError && sentry) {
    Sentry.captureException(lastError, {
      tags: {
        component: sentry.tag,
      },
      extra: sentry.extra ?? {},
    });
  }

  if (onFailure && !(skipFailureIfHandled && handledByRetry)) {
    onFailure(lastError);
  }

  throw lastError;
};
